import { Link } from "react-router-dom";
import type { Article } from "@/mocks/forum";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "compact" | "featured";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="flex items-start gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
      >
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {article.isPinned && (
              <span className="inline-flex items-center mr-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-accent-100 text-accent-700 align-middle">
                置顶
              </span>
            )}
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-400">
            <span>{article.views} 阅读</span>
            <span>{article.comments} 评论</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer group"
      >
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            {article.isPinned && (
              <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-accent-100 text-accent-700">
                置顶
              </span>
            )}
            {article.isHot && (
              <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-red-50 text-red-600">
                热门
              </span>
            )}
            <span className="text-xs text-foreground-400">{article.date}</span>
          </div>
          <h3 className="text-lg font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-4">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full overflow-hidden bg-background-200">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <span className="text-xs text-foreground-600 font-medium">{article.author}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line"></i>
                {article.comments}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-thumb-up-line"></i>
                {article.likes}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {article.isPinned && (
            <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-accent-100 text-accent-700">
              置顶
            </span>
          )}
          {article.isHot && (
            <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-red-50 text-red-600">
              热门
            </span>
          )}
          <span className="text-xs text-foreground-400">{article.date}</span>
        </div>
        <h3 className="text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-1.5">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-2 flex-wrap mb-2">
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded-md text-[11px] bg-secondary-100 text-secondary-700"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full overflow-hidden bg-background-200">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <span className="text-xs text-foreground-600">{article.author}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {article.comments}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-thumb-up-line"></i>
              {article.likes}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}